import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { z } from 'zod'
import {
  enumsQueryOptions,
  locationSearchQueryOptions,
  resolveCategoryIds,
  resolveRegionId,
} from '@/api/accommodation/hooks'
import { getBasePageBySlug } from '@/features/landing/registry'
import { toSearchParams } from '@/features/landing/searchParams'
import { LandingContent } from '@/features/landing/components/LandingContent'
import { buildLandingHead } from '@/lib/seo/landingHead'
import { Button } from '@/components/ui/button'

const paramsSchema = z.object({
  landingSlug: z.string().min(1),
})

function LandingNotFound() {
  return (
    <div className="container flex min-h-[50vh] flex-col items-center justify-center py-16 text-center">
      <h1 className="mb-2 text-2xl font-semibold text-slate-700">Stránka nenalezena</h1>
      <p className="mb-6 max-w-sm text-slate-500">Tato stránka neexistuje nebo byla odstraněna.</p>
      <Button asChild>
        <Link to="/pobyty">Zpět na tipy na ubytování</Link>
      </Button>
    </div>
  )
}

export const Route = createFileRoute('/_layout/pobyty/$landingSlug/')({
  component: LandingRoutePage,
  notFoundComponent: LandingNotFound,
  params: {
    parse: (params) => paramsSchema.parse(params),
  },
  loader: async ({ context, params }) => {
    const page = getBasePageBySlug(params.landingSlug)
    if (!page || page.kind === 'combo') throw notFound()
    // Enums resolve region/category slug -> ID for the /offer endpoint; awaited
    // so the first offer fetch is already filtered.
    const enums = await context.queryClient.query({
      ...enumsQueryOptions(),
      staleTime: 'static',
    })
    // prefetch (not ensure): a failing offer API must not turn the page into an
    // SSR 500; the grid falls back to fetching on the client.
    const searchParams = toSearchParams(page)
    await context.queryClient
      .query(
        locationSearchQueryOptions(
          searchParams,
          resolveRegionId(searchParams.region, enums.regions),
          resolveCategoryIds(searchParams.category, enums.locationCategories),
        ),
      )
      .catch(() => undefined)
    return page
  },
  head: ({ loaderData }) => (loaderData ? buildLandingHead(loaderData) : {}),
})

function LandingRoutePage() {
  const page = Route.useLoaderData()
  return <LandingContent page={page} />
}
